import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Card, Heading, vars } from '@etg/wings';
import { Overlay } from '@eti/components';
import { cabinBaggage, checkInBaggage } from '@eti/products';
import { useProperty, useSiteContext, useSnowplowTracking, useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries, resetButtonStyle } from '@eti/styles';
import {
  AncillaryCarrierPriceBreakdown,
  BoundNew,
  BundleProduct,
  CabinBaggageTripSummary,
  CheckedBaggageSummary,
  PersonalItemTripSummary,
  PriceFormat,
  TravelerNew,
  TripCharacteristics,
} from '@eti/types';
import { constants, phone } from '@eti/utils';
import Media from 'react-media';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import { usePortal } from '../../../../common/custom-hooks/portal';
import StickySidebar from '../../../../widgets/sidebar/components/StickySidebar';
import CartContactInfo from '../../../../widgets/cart/components/CartContactInfo';
import UiCart from '../../../../widgets/cart/components/UiCart';
import useIncludedOrUnavailableCheckInBaggage from '../../../../widgets/cart/hooks/useIncludedOrUnavailableCheckInBaggage';
import useIncludedOrUnavailableCabinBaggage from '../../../../widgets/cart/hooks/useIncludedOrUnavailableCabinBaggage';
import useIncludedOrUnavailablePersonalItem from '../../../../widgets/cart/hooks/useIncludedOrUnavailablePersonalItem';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../../utils/carriers';
import useAddedCheckInBaggage from '../../hooks/useAddedCheckedBaggage';
import useAddedCabinBaggage from '../../hooks/useAddedCabinBaggage';
import {
  getMinNumberOfSeats,
  shouldShowSeatsLeftNotification,
} from '../../../result/utils/resultUtils';
import CartTripInfo from '../../../../widgets/cart/components/trip-details/CartTripInfo';
import useNameFormatter from '../../../../common/custom-hooks/useNameFormatter';
import useCountries from '../../hooks/useCountries';

const mobileCartStyles = css`
  border-top: 1px solid ${etiColors.grayScale[4.5]};
  padding: 0;

  @media ${mediaQueries.medium.up} {
    padding: 0;
  }
`;

const formatCountry = (country) => ({
  ...country,
  prefix: country.phonePrefixes[0]?.prefix,
});

const getAgentPhoneCountryCode = (customerContactInfo, countries) => {
  const country = countries
    .map(formatCountry)
    .find((c) => c.prefix === customerContactInfo.phone.countryCallingCode);

  return `+${country.prefix}`;
};

const mobileCartTriggerStyles = css`
  color: ${vars.colors.links.default};
  display: block;
  font-size: inherit;
  margin: 0 auto;
  ${resetButtonStyle};
  position: relative;
  text-decoration: underline;
  z-index: 1;

  &:hover {
    color: ${vars.colors.links.focus};
  }

  &::after {
    border: 2px solid ${vars.colors.links.default};
    border-radius: 6px;
    content: '';
    height: calc(100% + 10px);
    left: -5px;
    opacity: 0;
    position: absolute;
    top: -5px;
    transition: opacity 0.2s ease-in-out;
    width: calc(100% + 10px);
    z-index: -1;
  }

  &:focus-visible {
    &::after {
      opacity: 1;
    }
  }
`;

const PaymentCart = ({
  bounds = [],
  bundles,
  cabinBaggageTripSummary,
  sidebarRef,
  chargeCurrencyPriceFormat,
  checkedBaggageSummary,
  customerEmail,
  customerPhone,
  customerContactInfo,
  discountAmount,
  headerAndInfoHeight,
  isMultiStop,
  instalmentFee,
  isOtherChargeCurrency,
  paymentMethodPrices,
  personalItemTripSummary,
  priceInChargeCurrency,
  products,
  selectedPaymentMethod,
  serviceFee,
  shouldAddInstallmentFeeInTotalAmount,
  subTotalPrice,
  totalPrice,
  travelerPrices,
  travelers,
  tripCharacteristics,
  tripProviderSystems,
  includedExtraProducts,
  tripType,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { agencyAccount } = useSiteContext();
  const { countries } = useCountries();
  const mobileTripDetailsButton = usePortal('mobileTripDetailsButton');
  const { isOpen, handleOpen, handleClose } = useToggle(false);
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const includedOrUnavailableCheckInBaggage = useIncludedOrUnavailableCheckInBaggage(
    travelerPrices,
    checkedBaggageSummary,
    null,
    null,
    products,
  );
  const includedOrUnavailableCabinBaggage = useIncludedOrUnavailableCabinBaggage(
    travelerPrices,
    cabinBaggageTripSummary,
  );
  const includedOrUnavailablePersonalItem = useIncludedOrUnavailablePersonalItem(
    personalItemTripSummary,
    travelerPrices,
  );

  const addedCheckInBaggage = useAddedCheckInBaggage(travelers, checkedBaggageSummary, products);

  const addedCabinBaggage = useAddedCabinBaggage(travelers, cabinBaggageTripSummary);
  const { formatName } = useNameFormatter();

  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    p('BookingFlow.SupplierCostMarkupBreakdown.Carriers'),
    p('BookingFlow.HideTaxIfZero.Carriers'),
  );
  const seatsThreshold = p('Air.Result.ShowNumberOfSeatsLeft.WhenLessThan');
  const numberOfSeatsLeft = getMinNumberOfSeats(bounds);
  const numberOfTravelers = travelers?.length;
  const shouldShowSeatsLeftNotificationMessage = shouldShowSeatsLeftNotification(
    numberOfSeatsLeft,
    numberOfTravelers,
    seatsThreshold,
  );

  const bundleContents =
    bundles?.length > 0
      ? bundles[0].options?.find((option) => option.id === bundles[0].selectedOption)
      : null;
  const bundleBaggageDetails =
    bundleContents &&
    bundleContents.content.find(
      (bundleContent) => bundleContent.productTypeId === checkInBaggage.id.toString(),
    );
  const bundleCabinBaggageDetails =
    bundleContents &&
    bundleContents.content.find(
      (bundleContent) => bundleContent.productTypeId === cabinBaggage.id.toString(),
    );

  const isCabinBaggagePresent =
    (addedCabinBaggage && addedCabinBaggage.length && !addedCabinBaggage[0].noBaggageSelected) ||
    (includedOrUnavailableCabinBaggage &&
      includedOrUnavailableCabinBaggage.length &&
      !includedOrUnavailableCabinBaggage[0].noBaggageAvailable);

  const isCheckedBaggagePresent =
    (addedCheckInBaggage &&
      addedCheckInBaggage.length &&
      !addedCheckInBaggage[0].noBaggageSelected) ||
    (includedOrUnavailableCheckInBaggage &&
      includedOrUnavailableCheckInBaggage.length &&
      !includedOrUnavailableCheckInBaggage[0].noBaggageAvailable);

  const handleSendTrackEvent = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Payment',
        action: 'Click',
        label: 'Travel details button',
      });
    }
  };

  const contactInformation = (
    <CartContactInfo
      className={constants.MOUSE_FLOW_CLASS_NAMES}
      id="paymentCart-contactInformation"
      label={
        agencyAccount
          ? t('AffiliateTravelAgency.Column.AgentInformation.Label')
          : t('Cart.ContactInformation.Label')
      }
    >
      {agencyAccount ? (
        <>
          <CartContactInfo.Row content={`${agencyAccount.name} – ${agencyAccount.code}`} />
          <CartContactInfo.Row
            content={formatName(customerContactInfo.firstName, customerContactInfo.lastName)}
          />
          <CartContactInfo.Row content={customerContactInfo.email} />
          <CartContactInfo.Row
            content={`${getAgentPhoneCountryCode(customerContactInfo, countries)} ${
              customerContactInfo.phone.numberWithoutCountryCode
            }`}
          />
        </>
      ) : (
        <>
          {customerEmail && <CartContactInfo.Row content={customerEmail} />}
          {customerPhone && <CartContactInfo.Row content={phone.addPrefix(customerPhone)} />}
        </>
      )}
    </CartContactInfo>
  );

  return (
    <Media query={mediaQueries.large.up}>
      {(matches) =>
        matches ? (
          <StickySidebar containerRef={sidebarRef} topSpacing={headerAndInfoHeight}>
            <UiCart
              bounds={bounds}
              bundleBaggageDetails={bundleBaggageDetails}
              bundleCabinBaggageDetails={bundleCabinBaggageDetails}
              bundles={bundles}
              cabinBaggageSelection={includedOrUnavailableCabinBaggage || addedCabinBaggage}
              chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
              checkedBaggageSelection={includedOrUnavailableCheckInBaggage || addedCheckInBaggage}
              checkedBaggageSummary={checkedBaggageSummary}
              contactInformation={contactInformation}
              dataTestId="payment-desktop-cart"
              discountAmount={discountAmount}
              handleSendTrackEvent={handleSendTrackEvent}
              includedExtraProducts={includedExtraProducts}
              instalmentFee={instalmentFee}
              isCabinBaggagePresent={isCabinBaggagePresent}
              isCheckedBaggagePresent={isCheckedBaggagePresent}
              isMultiStop={isMultiStop}
              isOtherChargeCurrency={isOtherChargeCurrency}
              marketingCarrierNames={marketingCarrierNames}
              numberOfSeatsLeft={numberOfSeatsLeft}
              paymentMethods={paymentMethodPrices}
              personalItem={includedOrUnavailablePersonalItem}
              priceInChargeCurrency={priceInChargeCurrency}
              products={products}
              providerSystems={tripProviderSystems}
              selectedPaymentMethod={selectedPaymentMethod}
              serviceFee={serviceFee}
              shouldAddInstallmentFeeInTotalAmount={shouldAddInstallmentFeeInTotalAmount}
              shouldShowSeatsLeftNotificationMessage={shouldShowSeatsLeftNotificationMessage}
              subTotalPrice={subTotalPrice}
              title={t('Cart.Title')}
              totalPrice={totalPrice}
              travelerPrices={travelerPrices}
              tripCharacteristics={tripCharacteristics}
              type={tripType}
            />
          </StickySidebar>
        ) : (
          <>
            {createPortal(
              <Card>
                <Card.Section>
                  <Heading data-testid="paymentPage-yourTrip-title" level={2}>
                    {t('PaymentPage.YourTrip.Title')}
                  </Heading>
                </Card.Section>
                <Card.Section>
                  <CartTripInfo
                    bounds={bounds}
                    dataTestId="paymentPage-yourTrip-information"
                    isMultiStop={isMultiStop}
                  />
                  <button
                    className={mobileCartTriggerStyles}
                    data-testid="payment-mobile-cart-trigger"
                    onClick={handleOpen}
                    type="button"
                  >
                    {t('Payment.MobileCart.TripSummary.Trigger.Label')}
                  </button>
                </Card.Section>
              </Card>,
              mobileTripDetailsButton,
            )}
            <Overlay.Drawer handleClose={handleClose} isOpen={isOpen} shouldPreventScroll>
              <Overlay.Header onClose={handleClose} title={t('Cart.Title')} />
              <Overlay.Content className={mobileCartStyles}>
                <UiCart
                  bounds={bounds}
                  bundleBaggageDetails={bundleBaggageDetails}
                  bundleCabinBaggageDetails={bundleCabinBaggageDetails}
                  bundles={bundles}
                  cabinBaggageSelection={includedOrUnavailableCabinBaggage || addedCabinBaggage}
                  chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
                  checkedBaggageSelection={
                    includedOrUnavailableCheckInBaggage || addedCheckInBaggage
                  }
                  checkedBaggageSummary={checkedBaggageSummary}
                  contactInformation={contactInformation}
                  dataTestId="payment-mobile-cart"
                  discountAmount={discountAmount}
                  handleSendTrackEvent={handleSendTrackEvent}
                  instalmentFee={instalmentFee}
                  isCabinBaggagePresent={isCabinBaggagePresent}
                  isCheckedBaggagePresent={isCheckedBaggagePresent}
                  isMultiStop={isMultiStop}
                  isOtherChargeCurrency={isOtherChargeCurrency}
                  marketingCarrierNames={marketingCarrierNames}
                  numberOfSeatsLeft={numberOfSeatsLeft}
                  paymentMethods={paymentMethodPrices}
                  personalItem={includedOrUnavailablePersonalItem}
                  priceInChargeCurrency={priceInChargeCurrency}
                  products={products}
                  selectedPaymentMethod={selectedPaymentMethod}
                  serviceFee={serviceFee}
                  shouldAddInstallmentFeeInTotalAmount={shouldAddInstallmentFeeInTotalAmount}
                  shouldShowSeatsLeftNotificationMessage={shouldShowSeatsLeftNotificationMessage}
                  subTotalPrice={subTotalPrice}
                  totalPrice={totalPrice}
                  travelerPrices={travelerPrices}
                  tripCharacteristics={tripCharacteristics}
                  type={tripType}
                />
              </Overlay.Content>
            </Overlay.Drawer>
          </>
        )
      }
    </Media>
  );
};

PaymentCart.propTypes = {
  bounds: PropTypes.arrayOf(BoundNew),
  bundles: PropTypes.arrayOf(BundleProduct),
  cabinBaggageTripSummary: CabinBaggageTripSummary,
  chargeCurrencyPriceFormat: PriceFormat,
  checkedBaggageSummary: CheckedBaggageSummary,
  customerContactInfo: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phone: PropTypes.shape({
      countryCallingCode: PropTypes.string,
      numberWithoutCountryCode: PropTypes.string,
    }),
  }),
  customerEmail: PropTypes.string,
  customerPhone: PropTypes.string,
  discountAmount: PropTypes.number,
  headerAndInfoHeight: PropTypes.number,
  includedExtraProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      texts: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ),
  instalmentFee: PropTypes.number,
  isMultiStop: PropTypes.bool,
  isOtherChargeCurrency: PropTypes.bool,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      serviceFee: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  personalItemTripSummary: PersonalItemTripSummary,
  priceInChargeCurrency: PropTypes.number,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      ancillaryCarrierPriceBreakdowns: PropTypes.arrayOf(AncillaryCarrierPriceBreakdown),
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      markup: PropTypes.number,
      name: PropTypes.string,
      numberOfUnits: PropTypes.number,
      price: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  selectedPaymentMethod: PropTypes.shape({
    label: PropTypes.string,
    price: PropTypes.number,
    serviceFee: PropTypes.number,
    type: PropTypes.string,
  }),
  serviceFee: PropTypes.shape({
    currency: PropTypes.shape({
      code: PropTypes.string,
      exponent: PropTypes.number,
      name: PropTypes.string,
    }),
    value: PropTypes.number,
  }),
  shouldAddInstallmentFeeInTotalAmount: PropTypes.bool,
  sidebarRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  subTotalPrice: PropTypes.number,
  totalPrice: PropTypes.number,
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      firstName: PropTypes.string,
      fullName: PropTypes.string,
      lastName: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  travelers: PropTypes.arrayOf(TravelerNew),
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripProviderSystems: PropTypes.arrayOf(PropTypes.string),
  tripType: PropTypes.string,
};
export default PaymentCart;
